/**
 * Push notification API — Guest Comms stage 1, Inc 5b (pass 2).
 *
 * JWT endpoints → shared `secureApi` instance (CLAUDE.md dual-axios rule). Core-staff only
 * server-side; an owner gets 403 from the router, never a filtered view.
 *
 * FEATURE-AGNOSTIC, mirroring the backend (D79). Nothing here mentions conversations or guests: it
 * manages devices and per-type preferences. The guest-message trigger lives with the webhook.
 *
 * FLAG BEHAVIOUR — deliberately different from `conversationApi`. That router is not mounted at all
 * when its flag is off, so the frontend has to recognise a bare HTML 404. This router IS mounted
 * unconditionally, so `GET /config` always answers honestly with `enabled: false` and everything
 * else returns a JSON 403. The Settings panel therefore never has to guess whether push is switched
 * off or the deploy is broken — it asks.
 *
 * ⚠️ `preserveBackendError: true` IS REQUIRED ON EVERY CALL THAT RENDERS AN ERROR, and the reason is
 * counter-intuitive enough to be worth stating. Errors here are identified by `code` (PUSH_xxx,
 * mirrored in `utils/notificationErrorCodes.js`) — but `secureApi`'s response interceptor REPLACES
 * `error.response.data` wholesale on 403/404/500 with a generic `{message, code:'ACCESS_DENIED'|
 * 'NOT_FOUND'|'SERVER_ERROR'}` unless this flag opts out. So omitting it does not merely lose the
 * server's Arabic string — it destroys the very `code` this layer depends on, and three of the seven
 * PUSH codes (`PUSH_001` 403, `PUSH_004` and `PUSH_005` 404) silently degrade to a generic
 * "unexpected error". Found by audit 2026-08-01, after an earlier version of this comment asserted
 * the opposite. `conversationApi.js` sets the flag for the same reason.
 *
 * Codes that arrive on other statuses (400, 409, 429, 502) were never rewritten, which is exactly why
 * this was invisible in manual testing — the one failure exercised by hand was a 502.
 */
import { secureApi } from './secureApi';

const BASE = '/api/v1/notifications';

// Every call that can surface a message to the operator carries this. See the header.
const KEEP_CODES = { preserveBackendError: true };

/**
 * Pull the stable error code out of a failed request.
 *
 * Returns null when there is no code — a network failure, a proxy error page, or an endpoint that
 * predates the code convention. Callers must handle null with a generic message rather than
 * assuming a code is always present.
 */
export const errorCodeOf = (err) => err?.response?.data?.code || null;

export const notificationApi = {
  /** Flag state, VAPID public key, and the type registry. Answers even when push is off. */
  config: () => secureApi.get(`${BASE}/config`, KEEP_CODES).then((r) => r.data),

  /** My devices + my per-type preferences. 403 when the flag is off. */
  me: () => secureApi.get(`${BASE}/me`, KEEP_CODES).then((r) => r.data),

  /**
   * Register this browser. Returns `{ deviceId }` — the handle this browser keeps so a later
   * notification tap can be attributed to this handset without the push endpoint (a bearer
   * capability) ever coming back over the wire.
   */
  subscribe: ({ endpoint, keys }) =>
    secureApi.post(`${BASE}/subscriptions`, { endpoint, keys }, KEEP_CODES).then((r) => r.data),

  removeDevice: (deviceId) =>
    secureApi.delete(`${BASE}/subscriptions/${deviceId}`, KEEP_CODES).then((r) => r.data),

  setPreference: (type, enabled) =>
    secureApi.put(`${BASE}/preferences/${type}`, { enabled }, KEEP_CODES).then((r) => r.data),

  /** «أرسل لي إشعارًا تجريبيًا» — the D91 install-session gate. Rate-limited to 5/min server-side. */
  test: () => secureApi.post(`${BASE}/test`, {}, KEEP_CODES).then((r) => r.data),

  /**
   * "A notification is why I am here" (D88).
   *
   * Fire-and-forget at every call site: it is measurement, and nothing on screen may wait on it or
   * break because of it. `deviceId` is optional — a browser that has not registered still reports
   * the open, it just cannot say which device.
   */
  recordOpen: ({ subjectKind, subjectId, deviceId }) =>
    secureApi
      .post(`${BASE}/opened`, { subjectKind, subjectId, deviceId: deviceId || null })
      .then((r) => r.data)
};

export default notificationApi;
